import React, { useEffect, useMemo, useState } from "react";
import { D, st } from "../../constants";

let nextZoneId = 1;
let nextSceneId = 1;
let nextKeypadId = 1;
let nextButtonId = 1;

function newZone(n) {
  return { id: nextZoneId++, name: `Zone ${n}`, loadIds: [] };
}
function newScene(n) {
  return { id: nextSceneId++, name: `Scene ${n}`, levels: {}, loadIds: [], defaultLevel: 100 };
}
function buildGlobalScenes() {
  return [
    { id: nextSceneId++, name: "All On", isGlobal: true, defaultLevel: 100 },
    { id: nextSceneId++, name: "All Off", isGlobal: true, defaultLevel: 0 },
  ];
}
function newButton(n, actionType, actionId, level) {
  return { id: nextButtonId++, label: `${n}`, actionType: actionType || "scene", actionId: actionId ?? null, level: level ?? 100 };
}
function newKeypad(n, name, buttons) {
  return { id: nextKeypadId++, name: name || `Keypad ${n}`, buttons: buttons || [] };
}

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getDimmedLoads(loadScheduleData) {
  const modules = loadScheduleData?.modules || [];
  const loads = [];
  modules.forEach((m) => {
    (m.loads || []).forEach((l) => {
      if (l.dimmed && (l.desc?.trim() || Number(l.watts) > 0)) {
        loads.push({ id: l.id, label: `${m.name}: ${l.desc || "(unnamed load)"}` });
      }
    });
  });
  return loads;
}

export default function ZonesScenes({ loadScheduleData, initialData, onChange }) {
  const [initial] = useState(() => {
    if (initialData?.globalScenes?.length || initialData?.scenes?.length || initialData?.zones?.length || initialData?.keypads?.length) {
      return {
        zones: initialData.zones?.length ? initialData.zones : [newZone(1)],
        globalScenes: initialData.globalScenes?.length ? initialData.globalScenes : buildGlobalScenes(),
        scenes: initialData.scenes?.length ? initialData.scenes : [newScene(1)],
        keypads: initialData.keypads?.length ? initialData.keypads : [newKeypad(1)],
      };
    }
    const globalScenes = buildGlobalScenes();
    const buttons = [
      newButton(1, "global", globalScenes[0].id),
      newButton(2, "global", globalScenes[1].id),
    ];
    return { zones: [newZone(1)], globalScenes, scenes: [newScene(1)], keypads: [newKeypad(1, "Main Keypad", buttons)] };
  });

  const [zones, setZones] = useState(initial.zones);
  const [globalScenes] = useState(initial.globalScenes);
  const [scenes, setScenes] = useState(initial.scenes);
  const [keypads, setKeypads] = useState(initial.keypads);

  const dimmedLoads = useMemo(() => getDimmedLoads(loadScheduleData), [loadScheduleData]);
  const loadById = (id) => dimmedLoads.find((l) => l.id === id);

  useEffect(() => {
    onChange?.({ zones, globalScenes, scenes, keypads });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones, globalScenes, scenes, keypads]);

  const addZone = () => setZones((prev) => [...prev, newZone(prev.length + 1)]);
  const removeZone = (id) => setZones((prev) => prev.filter((z) => z.id !== id));
  const updateZone = (id, patch) => setZones((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z)));
  const toggleZoneLoad = (zoneId, loadId) =>
    setZones((prev) =>
      prev.map((z) =>
        z.id === zoneId
          ? { ...z, loadIds: z.loadIds.includes(loadId) ? z.loadIds.filter((id) => id !== loadId) : [...z.loadIds, loadId] }
          : z
      )
    );

  const addScene = () => setScenes((prev) => [...prev, newScene(prev.length + 1)]);
  const removeScene = (id) => setScenes((prev) => prev.filter((s) => s.id !== id));
  const updateScene = (id, patch) => setScenes((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const setSceneLevel = (sceneId, loadId, level) =>
    setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, levels: { ...s.levels, [loadId]: level } } : s)));
  const removeLoadFromScene = (sceneId, loadId) =>
    setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, loadIds: s.loadIds.filter((id) => id !== loadId) } : s)));
  const addLoadToScene = (sceneId, loadId) =>
    setScenes((prev) => prev.map((s) => (s.id === sceneId ? { ...s, loadIds: [...s.loadIds, loadId] } : s)));
  const addZoneLoadsToScene = (sceneId, zoneId) => {
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone) return;
    setScenes((prev) =>
      prev.map((s) => (s.id === sceneId ? { ...s, loadIds: [...new Set([...s.loadIds, ...zone.loadIds])] } : s))
    );
  };

  const addKeypad = () => setKeypads((prev) => [...prev, newKeypad(prev.length + 1)]);
  const removeKeypad = (id) => setKeypads((prev) => prev.filter((k) => k.id !== id));
  const updateKeypadName = (id, name) => setKeypads((prev) => prev.map((k) => (k.id === id ? { ...k, name } : k)));
  const addButton = (keypadId) =>
    setKeypads((prev) => prev.map((k) => (k.id === keypadId ? { ...k, buttons: [...k.buttons, newButton(k.buttons.length + 1)] } : k)));
  const removeButton = (keypadId, buttonId) =>
    setKeypads((prev) => prev.map((k) => (k.id === keypadId ? { ...k, buttons: k.buttons.filter((b) => b.id !== buttonId) } : k)));
  const updateButton = (keypadId, buttonId, patch) =>
    setKeypads((prev) =>
      prev.map((k) =>
        k.id === keypadId ? { ...k, buttons: k.buttons.map((b) => (b.id === buttonId ? { ...b, ...patch } : b)) } : k
      )
    );

  if (dimmedLoads.length === 0) {
    return (
      <div style={st.card}>
        <div style={st.h2}>Zones & Scenes</div>
        <div style={{ fontSize: 13, color: D.muted }}>
          No dimmed loads found yet. Go to the Load Schedule tab, check "Dimmed?" on the loads you want scene control over, and they'll show up here.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={st.card}>
        <div style={st.h2}>Zones & Scenes</div>
        <div style={{ fontSize: 13, color: D.muted }}>
          Zones group dimmed loads from Load Schedule by room/area — a convenient way to bulk-add loads to a scene.
          Scenes are custom, programmer-designed presets (e.g. "Dinner Party") independent of each other and of zones.
        </div>
      </div>

      <div style={{ ...st.h2, marginTop: 8 }}>Global Scenes</div>
      {globalScenes.map((scene) => (
        <div key={scene.id} style={st.card}>
          <div style={{ fontWeight: 600, fontSize: 15, color: D.text, marginBottom: 6 }}>{scene.name}</div>
          <div style={{ fontSize: 12, color: D.muted }}>
            Turns all {dimmedLoads.length} dimmed load{dimmedLoads.length === 1 ? "" : "s"} in this project to {scene.defaultLevel}%,
            regardless of module, circuit, or zone.
          </div>
        </div>
      ))}

      <div style={{ ...st.h2, marginTop: 28 }}>Zones</div>
      {zones.map((zone) => (
        <div key={zone.id} style={st.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <input value={zone.name} onChange={(e) => updateZone(zone.id, { name: capitalizeFirst(e.target.value) })} style={{ ...st.input, fontWeight: 600, width: 200 }} />
            {zones.length > 1 && <button style={st.btn("danger")} onClick={() => removeZone(zone.id)}>Remove</button>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {dimmedLoads.map((load) => (
              <label key={load.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: D.muted, background: D.surface, padding: "4px 10px", borderRadius: 6 }}>
                <input type="checkbox" checked={zone.loadIds.includes(load.id)} onChange={() => toggleZoneLoad(zone.id, load.id)} style={{ accentColor: D.accent }} />
                {load.label}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button style={st.btn()} onClick={addZone}>+ Add zone</button>

      <div style={{ ...st.h2, marginTop: 28 }}>Scenes</div>
      {scenes.map((scene) => {
        const availableToAdd = dimmedLoads.filter((l) => !scene.loadIds.includes(l.id));
        return (
          <div key={scene.id} style={st.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <input value={scene.name} onChange={(e) => updateScene(scene.id, { name: capitalizeFirst(e.target.value) })} style={{ ...st.input, fontWeight: 600, width: 200 }} />
              {scenes.length > 1 && <button style={st.btn("danger")} onClick={() => removeScene(scene.id)}>Remove</button>}
            </div>
            {scene.loadIds.map((loadId) => {
              const load = loadById(loadId);
              if (!load) return null;
              const level = scene.levels[loadId] ?? scene.defaultLevel ?? 100;
              return (
                <div key={loadId} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: D.muted, width: 220 }}>{load.label}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={level}
                    onChange={(e) => setSceneLevel(scene.id, loadId, Number(e.target.value))}
                    style={{ flex: 1, accentColor: D.accent }}
                  />
                  <span style={{ fontSize: 12, color: D.text, width: 40, textAlign: "right" }}>{level}%</span>
                  <button
                    style={{ ...st.btn("danger"), fontSize: 11, padding: "3px 8px", marginLeft: 0 }}
                    onClick={() => removeLoadFromScene(scene.id, loadId)}
                    title="Remove this load from this scene only"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {availableToAdd.length > 0 && (
                <select
                  value=""
                  onChange={(e) => e.target.value && addLoadToScene(scene.id, Number(e.target.value))}
                  style={{ ...st.input, width: 220, fontSize: 12 }}
                >
                  <option value="">+ Add load to this scene…</option>
                  {availableToAdd.map((load) => (
                    <option key={load.id} value={load.id}>{load.label}</option>
                  ))}
                </select>
              )}
              {zones.length > 0 && (
                <select
                  value=""
                  onChange={(e) => e.target.value && addZoneLoadsToScene(scene.id, Number(e.target.value))}
                  style={{ ...st.input, width: 220, fontSize: 12 }}
                >
                  <option value="">+ Add all loads from a zone…</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>{z.name} ({z.loadIds.length} load{z.loadIds.length === 1 ? "" : "s"})</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );
      })}
      <button style={st.btn()} onClick={addScene}>+ Add scene</button>

      <div style={{ ...st.h2, marginTop: 28 }}>Keypad Assignment</div>
      {keypads.map((kp) => (
        <div key={kp.id} style={st.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <input value={kp.name} onChange={(e) => updateKeypadName(kp.id, capitalizeFirst(e.target.value))} style={{ ...st.input, fontWeight: 600, width: 220 }} placeholder="e.g. Foyer Keypad" />
            {keypads.length > 1 && <button style={st.btn("danger")} onClick={() => removeKeypad(kp.id)}>Remove Keypad</button>}
          </div>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Button</th>
                <th style={st.th}>Type</th>
                <th style={st.th}>Target</th>
                <th style={st.th}>Level</th>
                <th style={st.th}></th>
              </tr>
            </thead>
            <tbody>
              {kp.buttons.map((btn) => (
                <tr key={btn.id}>
                  <td style={st.td}>
                    <input value={btn.label} onChange={(e) => updateButton(kp.id, btn.id, { label: e.target.value })} style={{ ...st.input, width: 90 }} />
                  </td>
                  <td style={st.td}>
                    <select
                      value={btn.actionType}
                      onChange={(e) => updateButton(kp.id, btn.id, { actionType: e.target.value, actionId: null })}
                      style={{ ...st.input, width: 120 }}
                    >
                      <option value="load">Load</option>
                      <option value="zone">Zone</option>
                      <option value="scene">Scene</option>
                      <option value="global">All On / Off</option>
                    </select>
                  </td>
                  <td style={st.td}>
                    {btn.actionType === "load" && (
                      <select value={btn.actionId || ""} onChange={(e) => updateButton(kp.id, btn.id, { actionId: e.target.value ? Number(e.target.value) : null })} style={st.input}>
                        <option value="">— Select load —</option>
                        {dimmedLoads.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                      </select>
                    )}
                    {btn.actionType === "zone" && (
                      <select value={btn.actionId || ""} onChange={(e) => updateButton(kp.id, btn.id, { actionId: e.target.value ? Number(e.target.value) : null })} style={st.input}>
                        <option value="">— Select zone —</option>
                        {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                      </select>
                    )}
                    {btn.actionType === "scene" && (
                      <select value={btn.actionId || ""} onChange={(e) => updateButton(kp.id, btn.id, { actionId: e.target.value ? Number(e.target.value) : null })} style={st.input}>
                        <option value="">— Select scene —</option>
                        {scenes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    )}
                    {btn.actionType === "global" && (
                      <select value={btn.actionId || ""} onChange={(e) => updateButton(kp.id, btn.id, { actionId: e.target.value ? Number(e.target.value) : null })} style={st.input}>
                        <option value="">— Select —</option>
                        {globalScenes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={st.td}>
                    {(btn.actionType === "zone" || btn.actionType === "load") && (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={btn.level}
                        onChange={(e) => updateButton(kp.id, btn.id, { level: Number(e.target.value) })}
                        style={{ ...st.input, width: 70 }}
                      />
                    )}
                  </td>
                  <td style={st.td}>
                    <button style={{ ...st.btn("danger"), fontSize: 11, padding: "3px 8px" }} onClick={() => removeButton(kp.id, btn.id)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button style={st.btn()} onClick={() => addButton(kp.id)}>+ Add button</button>
        </div>
      ))}
      <button style={st.btn("primary")} onClick={addKeypad}>+ Add keypad</button>
    </div>
  );
}
